import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { IMenuItem } from 'src/types/menu.types';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bin,
  IOption,
  Card as ItemCard,
  MoveCategoryIcon,
  Prompt,
  SaladIcon,
  SnackbarStatus,
  useSnackbar,
  useTray,
  ViewIcon,
} from '@weareopenr/component-library';
import { MoveCategoryTrayContent } from '../MoveCategoryTray';
import { useState } from 'react';
import { deleteMenuItem } from 'src/api/deleteMenuItem/deleteMenuItem';
import { reorderMenuItem } from 'src/api/reorderMenuItem/reorderMenuItem';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getNextOpenId } from 'src/helpers/getNextOpenId';

export interface IMenuItemCardProps extends IMenuItem {
  menuId: string;
  parentItemId: string;
  sectionId?: string;
  displayOrderLeft?: number;
  displayOrderRight?: number;
}

const MenuItemCard = ({
  menuId,
  name,
  image,
  itemId,
  parentItemId,
  displayOrderLeft,
  displayOrderRight,
  sectionId,
  consumerName,
}: IMenuItemCardProps) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isMoveTrayOpen, setIsMoveTrayOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { openTray, closeTray } = useTray();
  const queryClient = useQueryClient();

  const { data: currentMenu, isFetching } = useMenu(menuId);

  const removePromptTitle = `Are you sure you want to remove "${name}"?`;
  const removeItemPromptSubTitle = `You will remove this product from the current menu.`;
  const isModifier = currentMenu && currentMenu.modifiers.find((modifier) => modifier.itemId === itemId);
  const multipleSections = currentMenu?.sections.length > 0;
  const redirectPath = `/items/${parentItemId}`;
  const navigate = useNavigate();
  const isPartOfBundle = (): boolean => {
    let isWithinBundle = false;
    currentMenu.sections.forEach((section) =>
      section.items.forEach((item) => {
        if (item.itemId === itemId && item.type !== 'BUNDLE') isWithinBundle = true;
      }),
    );

    currentMenu.uncategorised.forEach((item) => {
      if (item.itemId === itemId && item.type !== 'BUNDLE') isWithinBundle = true;
    });

    return isWithinBundle;
  };

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteMenuItem(itemId);
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully deleted "${name}".`,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (error) {
      if (isPartOfBundle()) {
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: error?.message,
        });
      } else {
        showSnackbar({
          status: SnackbarStatus.ERROR,
          message: 'We were unable to delete, please try again!',
        });
      }
      setIsRemovePromptVisible(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMovePrompt = () => {
    setIsMoveTrayOpen(true);
    openTray(
      <MoveCategoryTrayContent
        key={getNextOpenId()}
        menuId={menuId}
        itemId={itemId}
        sectionId={sectionId}
        onClose={closeTray}
      />,
      {
        title: `Move "${name}" product?`,
        // Runs on every close path, including the Tray's own X button (which skips the prop above).
        onClose: () => setIsMoveTrayOpen(false),
      },
    );
  };

  const reorderItem = async (newDisplayOrder: number) => {
    try {
      await reorderMenuItem({ itemId, newDisplayOrder });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  const ellipsisOptions: IOption[] = [
    {
      id: 0,
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => {
        navigate(redirectPath);
      },
    },
  ];
  if (!isModifier) {
    ellipsisOptions.push({
      id: 1,
      name: 'Move to section',
      icon: MoveCategoryIcon,
      onOptionClick: handleMovePrompt,
      isDisabled: !multipleSections || isMoveTrayOpen,
    });
  }

  if (sectionId) {
    ellipsisOptions.push(
      {
        id: 2,
        name: 'Move left',
        icon: ArrowLeftIcon,
        onOptionClick: () => reorderItem(displayOrderLeft),
        isDisabled: displayOrderLeft === undefined,
        dataTestId: 'move-left',
        persistMenuOnClick: true,
      },
      {
        id: 3,
        name: 'Move right',
        icon: ArrowRightIcon,
        onOptionClick: () => reorderItem(displayOrderRight),
        isDisabled: displayOrderRight === undefined,
        dataTestId: 'move-right',
        persistMenuOnClick: true,
      },
    );
  }

  ellipsisOptions.push(
    {
      id: 'd-1',
      isDivider: true,
    },
    {
      id: 4,
      name: 'Remove',
      icon: Bin,
      onOptionClick: () => setIsRemovePromptVisible(true),
      hoverColour: 'tomato',
      isDisabled: isMoveTrayOpen,
    },
  );
  return (
    <>
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={() => setIsRemovePromptVisible(false)}
        open={isRemovePromptVisible}
        buttonHandler={handleDelete}
        buttonText={'Remove'}
        buttonColour="tomato"
        backButton={true}
        size="small"
        isSubmitting={isSubmitting || isFetching}
      >
        {removeItemPromptSubTitle}
      </Prompt>
      <ItemCard
        text={name}
        key={itemId}
        imageSrc={image}
        to={redirectPath}
        ellipsisOptions={ellipsisOptions}
        subtext={consumerName}
        icon={SaladIcon}
        variant="quaternary"
        data-testid="card"
      />
    </>
  );
};

export default MenuItemCard;
